import {
  AddEntityTradesToDealRequestPayload,
  AllHedgingInstruments,
  CreateUpdateDealRequestPayload,
  DealSummaryReportRequestPayload,
  DownloadDealTermSheetRequest,
  GetEntityTradesRequestPayload,
  GetGoldVisionDealRequestPayload,
  PatchPayload,
} from '@/pages/Deal/store/types';
import {
  DealDocument,
  DealTrade,
  ExecutionCriteriaItem,
} from '@pages/Deal/types';
import {
  DonloadExecutionSummaryPayload,
  GetGoldVisionDealsRequestPayload,
  GoldVisionResyncStatusResponse,
  UpdateDepartmentsPayload,
} from '@pages/Deals/store/actions';
import { GetDealsDataResponse } from '@pages/Deals/types';
import { AxiosInstance, AxiosResponse } from 'axios';
import { pick } from 'lodash';

import { ApiResponse } from '../../models/ApiResponse';
import { EntityTrade } from '../../models/EntityTrade';
import {
  DealCenterFlag,
  GoldVisionDeal,
} from '../../models/GoldVision/GoldVisionDeal';

export default class DealsAPI {
  client: AxiosInstance;
  constructor(client: AxiosInstance) {
    this.client = client;
  }

  //deals
  async getGoldvisionOpenData(
    payload: GetGoldVisionDealsRequestPayload,
    config?: { signal?: AbortSignal },
  ): Promise<AxiosResponse<GetDealsDataResponse>> {
    return this.client.post(
      `/v2/organizations/gold-vision/relationship-open/${payload.page_number}/${payload.lines_per_page}/`,
      {
        proposal_id: payload.proposal_id,
        company_ids: payload.company_ids,
        owners: payload.owners,
        statuses: payload.statuses,
        department_ids: payload.departments,
        summary: payload.summary,
        created_from: payload.created_from,
        created_to: payload.created_to,
      },
      { signal: config?.signal },
    );
  }

  async getGoldvisionClosedData(
    payload: GetGoldVisionDealsRequestPayload,
    config?: { signal?: AbortSignal },
  ): Promise<AxiosResponse<GetDealsDataResponse>> {
    return this.client.post(
      `/v2/organizations/gold-vision/relationship-closed/${payload.page_number}/${payload.lines_per_page}/`,
      {
        proposal_id: payload.proposal_id,
        company_ids: payload.company_ids,
        owners: payload.owners,
        statuses: payload.statuses,
        department_ids: payload.departments,
        summary: payload.summary,
        created_from: payload.created_from,
        created_to: payload.created_to,
      },
      { signal: config?.signal },
    );
  }

  async getGoldvisionAllData(
    payload: GetGoldVisionDealsRequestPayload,
    config?: { signal?: AbortSignal },
  ): Promise<AxiosResponse<GetDealsDataResponse>> {
    return this.client.post(
      `/v2/organizations/gold-vision/relationship-all/${payload.page_number}/${payload.lines_per_page}/`,
      {
        proposal_id: payload.proposal_id,
        company_ids: payload.company_ids,
        owners: payload.owners,
        statuses: payload.statuses,
        department_ids: payload.departments,
        summary: payload.summary,
        created_from: payload.created_from,
        created_to: payload.created_to,
      },
      { signal: config?.signal },
    );
  }

  //deal
  async getDealByGoldVisionId(
    payload: GetGoldVisionDealRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<GoldVisionDeal>>> {
    return this.client.get(
      `/v2/organizations/gold-vision/${payload.gold_vision_id}/deal/`,
    );
  }

  async getDealTradesByGoldVisionId(
    payload: GetGoldVisionDealRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<DealTrade[]>>> {
    return this.client.get(
      `/v2/organizations/gold-vision/${payload.gold_vision_id}/trades/`,
    );
  }

  async getExecutionCriteria(
    payload: GetGoldVisionDealRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<ExecutionCriteriaItem[]>>> {
    return this.client.get(
      `/v2/organizations/gold-vision/${payload.gold_vision_id}/execution-criteria/`,
    );
  }

  async getDealFiles(
    dealId: number,
  ): Promise<AxiosResponse<ApiResponse<DealDocument[]>>> {
    return this.client.get(`/v2/file-management/deal/${dealId}/files/`);
  }

  async fetchEntityTrades(
    payload: GetEntityTradesRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<EntityTrade[]>>> {
    return this.client.post(
      '/v2/hedging/transactions/entity-transactions-active-internal-and-closed/',
      payload,
    );
  }

  async fetchAddEntityTrades(
    payload: AddEntityTradesToDealRequestPayload,
  ): Promise<AxiosResponse<ApiResponse<EntityTrade[]>>> {
    return this.client.post(
      `/v2/organizations/gold-vision/${payload.gold_vision_id}/trades/`,
      pick(payload, 'trade_ids'),
    );
  }

  async createNewDeal(payload: CreateUpdateDealRequestPayload) {
    return this.client.post(
      '/v2/organizations/deal-center-deals-crud/',
      payload.data,
    );
  }
  async editDeal(payload: CreateUpdateDealRequestPayload) {
    return this.client.put(
      `/v2/organizations/deal-center-deals-crud/${payload.dealId}/`,
      payload.data,
    );
  }

  async fetchAllHedgingInstruments(): Promise<
    AxiosResponse<ApiResponse<AllHedgingInstruments[]>>
  > {
    return this.client.get('/v2/hedging/pricer-types/');
  }

  //todo finish
  async downloadDealSummaryReport(payload: DealSummaryReportRequestPayload) {
    return this.client.post(
      '/v2/organizations/download-deal-summary-report/',
      payload,
    );
  }

  // get deals departments
  async getDealsDepartments() {
    return this.client.get('/v2/organizations/gold-vision/departments/');
  }

  async partialDealUpdate(payload: PatchPayload) {
    return this.client.patch(
      `/v2/organizations/deal-center-deals-crud/${payload.dealId}/`,
      payload.data,
    );
  }

  async getDealCenterFlags(): Promise<
    AxiosResponse<ApiResponse<{ results: DealCenterFlag[] }>>
  > {
    return this.client.get('/v2/organizations/deal-center-deals/flags/');
  }

  async downloadDealTermSheet(payload: DownloadDealTermSheetRequest) {
    return this.client.post(
      '/v2/reporting/download-multi-termsheets/',
      payload,
    );
  }

  // Kick off a Gold Vision resync. Returns a human-readable status string,
  // e.g. "Gold Vision resync started." or "Gold Vision resync already in progress."
  async goldVisionDataRefresh(): Promise<AxiosResponse<ApiResponse<string>>> {
    return this.client.post('/v2/organizations/gold-vision/resync/');
  }

  // Poll whether a Gold Vision resync is currently running.
  async goldVisionResyncStatus(): Promise<
    AxiosResponse<GoldVisionResyncStatusResponse>
  > {
    return this.client.get('/v2/organizations/gold-vision/resync/');
  }

  async updateDepartments(payload: UpdateDepartmentsPayload) {
    return this.client.post(
      `/v2/organizations/gold-vision/${payload?.gold_vision_id}/department/refresh/`,
    );
  }

  async downloadExecutionSummary(payload: DonloadExecutionSummaryPayload) {
    return this.client.post(
      '/v2/organizations/execution-summary-report/',
      payload,
    );
  }
}
